import React, { useEffect, useRef, useState } from "react";
import PhoneInput from "../../../components/PhoneInput";
import { CiSaveDown2 } from "react-icons/ci";
import { FiMoon, FiRefreshCw, FiSun } from "react-icons/fi";
import { resolveAdminLocationMapsLink } from "../../../api/adminLocations";
import { toDateKey } from "../../../utils/dateUtils";
import { DEFAULT_LOCATION_FORM } from "../adminConstants";
import {
  formatCommissionRateInput,
  formatCurrency,
  formatLocationAddress,
  formatTimestamp,
} from "../adminUtils";

const LOCATION_WEEK_DAYS = [
  { key: "mon", label: "Mon", short: "Mo" },
  { key: "tue", label: "Tue", short: "Tue" },
  { key: "wed", label: "Wed", short: "Wed" },
  { key: "thu", label: "Thu", short: "Thu" },
  { key: "fri", label: "Fri", short: "Fri" },
  { key: "sat", label: "Sat", short: "Sat" },
  { key: "sun", label: "Sun", short: "Sun" },
];

const TIME_HOURS = Array.from({ length: 24 }, (_, index) =>
  String(index).padStart(2, "0")
);
const TIME_MINUTES = Array.from({ length: 60 }, (_, index) =>
  String(index).padStart(2, "0")
);

const createEmptyHours = () =>
  LOCATION_WEEK_DAYS.reduce(
    (acc, day) => ({
      ...acc,
      [day.key]: [],
    }),
    {}
  );

const normalizeTime = (value) => {
  const [hoursRaw, minutesRaw] = String(value || "").split(":");
  const hours = Number.parseInt(hoursRaw, 10);
  const minutes = Number.parseInt(minutesRaw, 10);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

  const splitTimeParts = (value) => {
    const [hoursRaw, minutesRaw] = String(value || "").split(":");
    if (!hoursRaw || !minutesRaw) {
      return { hour: "00", minute: "00" };
    }
    return {
      hour: String(hoursRaw).padStart(2, "0"),
      minute: String(minutesRaw).padStart(2, "0"),
    };
  };

const parseOperatingHours = (value) => {
  const next = createEmptyHours();
  if (!value) return next;
  const normalized = String(value).replace(/\s+/g, " ").trim();
  if (!normalized) return next;
  const labelIndex = new Map(
    LOCATION_WEEK_DAYS.map((day, index) => [day.label, index])
  );
  const parts = normalized
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean);
  parts.forEach((part) => {
    const match = part.match(
      /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)(?:-(Mon|Tue|Wed|Thu|Fri|Sat|Sun))?\s+(.+)$/
    );
    if (!match) return;
    const startLabel = match[1];
    const endLabel = match[2] || match[1];
    const rangesText = match[3] || "";
    const ranges = rangesText
      .split(",")
      .map((range) => range.trim())
      .filter(Boolean)
      .map((range) => {
        const rangeMatch = range.match(
          /(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/
        );
        if (!rangeMatch) return null;
        const startTime = normalizeTime(rangeMatch[1]);
        const endTime = normalizeTime(rangeMatch[2]);
        if (!startTime || !endTime) return null;
        return { start: startTime, end: endTime };
      })
      .filter(Boolean);

    if (ranges.length === 0) return;
    const startIndex = labelIndex.get(startLabel);
    const endIndex = labelIndex.get(endLabel);
    if (startIndex == null || endIndex == null || startIndex > endIndex) return;
    for (let i = startIndex; i <= endIndex; i += 1) {
      const dayKey = LOCATION_WEEK_DAYS[i].key;
      next[dayKey] = [...(next[dayKey] || []), ...ranges];
    }
  });
  return next;
};

const formatOperatingHours = (hoursByDay) => {
  const entries = LOCATION_WEEK_DAYS.map((day) => {
    const ranges = (hoursByDay?.[day.key] ?? [])
      .filter((range) => range?.start && range?.end)
      .map((range) => `${range.start}-${range.end}`);
    return {
      label: day.label,
      rangeText: ranges.join(", "),
    };
  });

  const groups = [];
  let index = 0;
  while (index < entries.length) {
    const entry = entries[index];
    if (!entry.rangeText) {
      index += 1;
      continue;
    }
    let endIndex = index;
    while (endIndex + 1 < entries.length) {
      const next = entries[endIndex + 1];
      if (next.rangeText !== entry.rangeText) break;
      endIndex += 1;
    }
    const rangeLabel =
      endIndex > index
        ? `${LOCATION_WEEK_DAYS[index].label}-${LOCATION_WEEK_DAYS[endIndex].label}`
        : LOCATION_WEEK_DAYS[index].label;
    groups.push(`${rangeLabel} ${entry.rangeText}`);
    index = endIndex + 1;
  }
  return groups.join("; ");
};

const parseMapsLinkCandidate = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    decoded = raw;
  }
  try {
    const url = new URL(decoded);
    const query =
      url.searchParams.get("q") ||
      url.searchParams.get("query") ||
      url.searchParams.get("q1") ||
      "";
    if (query) {
      return { address: query.replace(/\+/g, " ").trim(), isPlaceName: false };
    }
    const path = url.pathname || "";
    const placeMatch = path.match(/\/place\/([^/]+)/i);
    if (placeMatch && placeMatch[1]) {
      return { address: placeMatch[1].replace(/\+/g, " ").trim(), isPlaceName: true };
    }
    const searchMatch = path.match(/\/search\/([^/]+)/i);
    if (searchMatch && searchMatch[1]) {
      return { address: searchMatch[1].replace(/\+/g, " ").trim(), isPlaceName: true };
    }
  } catch {
    // Ignore URL parsing errors.
  }

  const inlineMatch = decoded.match(/[?&]q=([^&]+)/i) || decoded.match(/[?&]query=([^&]+)/i);
  if (inlineMatch && inlineMatch[1]) {
    return { address: inlineMatch[1].replace(/\+/g, " ").trim(), isPlaceName: false };
  }

  return null;
};

const isShortMapsLink = (value) => {
  if (!value) return false;
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    return host === "maps.app.goo.gl" || host === "goo.gl" || host === "g.co";
  } catch {
    return false;
  }
};

const normalizeBlockedDates = (values) =>
  [...new Set((Array.isArray(values) ? values : []).map((value) => toDateKey(value)).filter(Boolean))].sort();

const AdminLocPartnersTab = ({
  t,
  isActive,
  partnersRef,
  partnerFormOpen,
  handlePartnerSave,
  partnerForm,
  handlePartnerFormChange,
  partnerEditingId,
  handleGeneratePartnerPin,
  isSavingPartner,
  setPartnerFormOpen,
  setPartnerEditingId,
  setPartnerForm,
  locations,
  partnersLoading,
  partnersError,
  partners,
  partnerDetailsById,
  partnerDetailsLoadingId,
  partnerCommissionEdits,
  expandedPartnerId,
  handlePartnerDetailsToggle,
  handlePartnerEdit,
  handlePartnerAccessToggle,
  handlePartnerDelete,
  handlePartnerCommissionChange,
  handlePartnerCommissionSave,
  partnerPinLoadingId,
  revealedPins,
  handlePartnerResetPin,
  openPartnerForm,
  locationsRef,
  locationFormOpen,
  handleLocationSave,
  locationForm,
  handleLocationFormChange,
  isSavingLocation,
  locationEditingId,
  setLocationFormOpen,
  setLocationEditingId,
  setLocationForm,
  locationsLoading,
  locationsError,
  handleLocationEdit,
  handleLocationToggleActive,
  handleLocationDelete,
  openLocationForm,
  handleLocationsRefresh,
}) => {
  const handlePartnerPhoneChange = (value) => {
    setPartnerForm((prev) => ({
      ...prev,
      contactPhone: value,
    }));
  };
  const [expandedLocationId, setExpandedLocationId] = useState(null);
  const [mapsLink, setMapsLink] = useState("");
  const [mapsLinkError, setMapsLinkError] = useState("");
  const [mapsLinkWarning, setMapsLinkWarning] = useState("");
  const [mapsLinkLoading, setMapsLinkLoading] = useState(false);
  const mapsLinkRequestId = useRef(0);
  const [selectedDayKey, setSelectedDayKey] = useState(LOCATION_WEEK_DAYS[0].key);
  const [hoursByDay, setHoursByDay] = useState(() => createEmptyHours());
  const [hoursDirty, setHoursDirty] = useState(false);
  const [blockedDateInput, setBlockedDateInput] = useState("");

  const handleLocationDetailsToggle = (locationId) => {
    setExpandedLocationId((prev) => (prev === locationId ? null : locationId));
  };

  useEffect(() => {
    if (!locationFormOpen) return;
    setMapsLink("");
    setMapsLinkError("");
    setMapsLinkWarning("");
    setMapsLinkLoading(false);
    mapsLinkRequestId.current += 1;
    setSelectedDayKey(LOCATION_WEEK_DAYS[0].key);
    setHoursByDay(parseOperatingHours(locationForm.operatingHours));
    setHoursDirty(false);
    setBlockedDateInput("");
  }, [locationEditingId, locationForm.operatingHours, locationFormOpen]);

  useEffect(() => {
    if (!hoursDirty) return;
    const nextValue = formatOperatingHours(hoursByDay);
    setLocationForm((prev) =>
      prev.operatingHours === nextValue ? prev : { ...prev, operatingHours: nextValue }
    );
  }, [hoursByDay, hoursDirty, setLocationForm]);

  useEffect(() => {
    if (!locationFormOpen) return;
    const value = mapsLink.trim();
    const requestId = mapsLinkRequestId.current + 1;
    mapsLinkRequestId.current = requestId;
    setMapsLinkLoading(false);
    if (!value) {
      setMapsLinkError("");
      setMapsLinkWarning("");
      setMapsLinkLoading(false);
      return;
    }

    const candidate = parseMapsLinkCandidate(value);
    if (candidate && !candidate.isPlaceName) {
      setMapsLinkError("");
      setMapsLinkLoading(false);
      setLocationForm((prev) => ({ ...prev, address: candidate.address }));
      return;
    }
    setMapsLinkError("");
    if (isShortMapsLink(value)) {
      setMapsLinkWarning(
        t(
          "admin.locationForm.mapsLinkShortWarning",
          "Short URL WARNING, it can resolve to the wrong place"
        )
      );
    } else {
      setMapsLinkWarning("");
    }
    const timeoutId = setTimeout(async () => {
      setMapsLinkLoading(true);
      setMapsLinkError("");
      try {
        const resolved = await resolveAdminLocationMapsLink(value);
        if (mapsLinkRequestId.current !== requestId) return;
        const nextAddress = resolved?.address?.trim();
        if (!nextAddress) {
          setMapsLinkError(
            t(
              "admin.locationForm.mapsLinkResolveError",
              "We could not resolve an address from this link."
            )
          );
          if (candidate?.address) {
            setLocationForm((prev) => ({ ...prev, address: candidate.address }));
          }
          return;
        }
        const nextName = resolved?.name?.trim();
        const nextHours = resolved?.operatingHours?.trim();
        setLocationForm((prev) => ({
          ...prev,
          address: nextAddress,
          city: resolved.city ?? prev.city,
          postalCode: resolved.postalCode ?? prev.postalCode,
          country: resolved.country ?? prev.country,
          name: nextName || prev.name,
          operatingHours: nextHours || prev.operatingHours,
          latitude:
            resolved.latitude != null ? String(resolved.latitude) : prev.latitude,
          longitude:
            resolved.longitude != null ? String(resolved.longitude) : prev.longitude,
        }));
        if (nextHours) {
          setHoursByDay(parseOperatingHours(nextHours));
          setHoursDirty(false);
        }
      } catch (error) {
        if (mapsLinkRequestId.current !== requestId) return;
        setMapsLinkError(
          error?.message ||
            t(
              "admin.locationForm.mapsLinkResolveError",
              "We could not resolve an address from this link."
            )
        );
      } finally {
        if (mapsLinkRequestId.current === requestId) {
          setMapsLinkLoading(false);
        }
      }
    }, 600);

    return () => clearTimeout(timeoutId);
  }, [locationFormOpen, mapsLink, setLocationForm, t]);

  const handleMapsLinkChange = (event) => {
    const value = event.target.value;
    setMapsLink(value);
    if (!value.trim()) {
      setMapsLinkError("");
      setMapsLinkWarning("");
      setMapsLinkLoading(false);
    }
  };

  const handleTimePartChange = (field, rangeIndex, part) => (event) => {
    const value = event.target.value;
    setHoursByDay((prev) => {
      const ranges = [...(prev[selectedDayKey] ?? [])];
      if (ranges.length === 0) {
        ranges.push({ start: "", end: "" });
      }
      const currentRange = ranges[rangeIndex] || { start: "", end: "" };
      const currentValue = currentRange[field] ?? "";
      const parts = splitTimeParts(currentValue);
      const nextParts = { ...parts, [part]: value };
      const nextHour = nextParts.hour || "00";
      const nextMinute = nextParts.minute || "00";
      const nextValue = `${nextHour}:${nextMinute}`;
      ranges[rangeIndex] = { ...currentRange, [field]: nextValue };
      return {
        ...prev,
        [selectedDayKey]: ranges,
      };
    });
    setHoursDirty(true);
  };

  const handleAddRange = () => {
    setHoursByDay((prev) => {
      const ranges = [...(prev[selectedDayKey] ?? [])];
      ranges.push({ start: "", end: "" });
      return { ...prev, [selectedDayKey]: ranges };
    });
    setHoursDirty(true);
  };

  const handleRemoveRange = (indexToRemove) => {
    setHoursByDay((prev) => {
      const ranges = [...(prev[selectedDayKey] ?? [])];
      ranges.splice(indexToRemove, 1);
      return { ...prev, [selectedDayKey]: ranges };
    });
    setHoursDirty(true);
  };

  const handleHoursClear = () => {
    setHoursByDay((prev) => ({
      ...prev,
      [selectedDayKey]: [],
    }));
    setHoursDirty(true);
  };

  const handleBlockedDateAdd = () => {
    const nextDate = toDateKey(blockedDateInput);
    if (!nextDate) return;
    setLocationForm((prev) => ({
      ...prev,
      blockedDates: normalizeBlockedDates([...(prev.blockedDates || []), nextDate]),
    }));
    setBlockedDateInput("");
  };

  const handleBlockedDateRemove = (date) => {
    setLocationForm((prev) => ({
      ...prev,
      blockedDates: normalizeBlockedDates((prev.blockedDates || []).filter((item) => item !== date)),
    }));
  };

  const selectedDay =
    LOCATION_WEEK_DAYS.find((day) => day.key === selectedDayKey) ||
    LOCATION_WEEK_DAYS[0];
  const selectedRanges = hoursByDay[selectedDayKey] || [];
  const displayRanges =
    selectedRanges.length > 0 ? selectedRanges : [{ start: "", end: "" }];
  const blockedDates = normalizeBlockedDates(locationForm.blockedDates);

  return (
<div
              className={`admin-tab ${
                isActive ? "admin-tab--active" : ""
              }`.trim()}
            >
              <section className="admin-grid admin-animate admin-animate--delay-4">
          <article ref={partnersRef} className="admin-panel">
            <header>
              <h2>{t("admin.partners.heading", "Partners management")}</h2>
            </header>
            {partnerFormOpen && (
              <form className="admin-form" onSubmit={handlePartnerSave}>
                <div className="admin-form__grid">
                  <div className="admin-form__row">
                    <label className="admin-form__label" htmlFor="partnerName">
                      {t("admin.partnerForm.name", "Partner name")}
                    </label>
                    <input
                      id="partnerName"
                      className="admin-input"
                      value={partnerForm.name}
                      onChange={handlePartnerFormChange("name")}
                      placeholder={t("admin.partnerForm.namePlaceholder", "Harbor Cafe")}
                    />
                  </div>
                  <div className="admin-form__row">
                    <label className="admin-form__label" htmlFor="partnerLocation">
                      {t("admin.partnerForm.location", "Pickup location")}
                    </label>
                    <select
                      id="partnerLocation"
                      className="admin-select"
                      value={partnerForm.locationId}
                      onChange={handlePartnerFormChange("locationId")}
                    >
                      {locations.length === 0 ? (
                        <option value="">
                          {t("admin.partnerForm.noLocations", "No locations available")}
                        </option>
                      ) : (
                        locations.map((location) => (
                          <option key={location.id} value={location.id}>
                            {location.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <div className="admin-form__row">
                    <label className="admin-form__label" htmlFor="partnerContactName">
                      {t("admin.partnerForm.contactName", "Contact name")}
                    </label>
                    <input
                      id="partnerContactName"
                      className="admin-input"
                      value={partnerForm.contactName}
                      onChange={handlePartnerFormChange("contactName")}
                      placeholder={t("admin.partnerForm.contactNamePlaceholder", "Cafe manager")}
                    />
                  </div>
                  <div className="admin-form__row">
                    <label className="admin-form__label" htmlFor="partnerContactEmail">
                      {t("admin.partnerForm.contactEmail", "Contact email")}
                    </label>
                    <input
                      id="partnerContactEmail"
                      className="admin-input"
                      type="email"
                      value={partnerForm.contactEmail}
                      onChange={handlePartnerFormChange("contactEmail")}
                      placeholder={t(
                        "admin.partnerForm.contactEmailPlaceholder",
                        "manager@cafe.at"
                      )}
                    />
                  </div>
                  <div className="admin-form__row">
                    <label className="admin-form__label" htmlFor="partnerContactPhone">
                      {t("admin.partnerForm.contactPhone", "Contact phone")}
                    </label>
                    <PhoneInput
                      id="partnerContactPhone"
                      name="contactPhone"
                      value={partnerForm.contactPhone}
                      onChange={handlePartnerPhoneChange}
                      autoComplete="tel"
                      placeholder={t(
                        "admin.partnerForm.contactPhonePlaceholder",
                        "Phone number"
                      )}
                      className="admin-phone"
                      selectClassName="admin-select admin-select--compact"
                      inputClassName="admin-input"
                      countryLabel={t("admin.partnerForm.countryCode", "Country code")}
                      numberLabel={t("admin.partnerForm.contactPhone", "Contact phone")}
                    />
                  </div>
                  <div className="admin-form__row">
                    <label className="admin-form__label" htmlFor="partnerCommissionRate">
                      {t("admin.partnerForm.commissionRate", "Commission rate (%)")}
                    </label>
                    <input
                      id="partnerCommissionRate"
                      className="admin-input"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={partnerForm.commissionRate}
                      onChange={handlePartnerFormChange("commissionRate")}
                      placeholder={t("admin.partnerForm.commissionRatePlaceholder", "15")}
                    />
                  </div>
                  <div className="admin-form__row">
                    <label className="admin-form__label" htmlFor="partnerPin">
                      {partnerEditingId
                        ? t("admin.partnerForm.pinOptional", "New PIN (optional)")
                        : t("admin.partnerForm.pin", "Partner PIN")}
                    </label>
                    <input
                      id="partnerPin"
                      className="admin-input"
                      value={partnerForm.pin}
                      onChange={handlePartnerFormChange("pin")}
                      placeholder={t("admin.partnerForm.pinPlaceholder", "4 to 6 digits")}
                      inputMode="numeric"
                      maxLength={6}
                      pattern="[0-9]{4,6}"
                      title={t("admin.partnerForm.pinPlaceholder", "4 to 6 digits")}
                    />
                    <button
                      type="button"
                      className="admin-button admin-button--ghost"
                      onClick={handleGeneratePartnerPin}
                    >
                      {t("admin.partnerForm.generatePin", "Generate PIN")}
                    </button>
                  </div>
                </div>
                <div className="admin-form__actions">
                  <button
                    type="submit"
                    className="admin-button admin-button--primary"
                    disabled={isSavingPartner}
                  >
                    {partnerEditingId
                      ? t("admin.partnerForm.update", "Update partner")
                      : t("admin.partnerForm.save", "Save partner")}
                  </button>
                  <button
                    type="button"
                    className="admin-button admin-button--ghost"
                    onClick={() => {
                      setPartnerFormOpen(false);
                      setPartnerEditingId(null);
                      setPartnerForm({
                        name: "",
                        locationId: "",
                        pin: "",
                        contactName: "",
                        contactEmail: "",
                        contactPhone: "",
                        commissionRate: "15",
                      });
                    }}
                  >
                    {t("admin.partnerForm.cancel", "Cancel")}
                  </button>
                </div>
              </form>
            )}
            <ul className="admin-list">
              {partnersLoading ? (
                <li className="admin-empty">
                  {t("admin.partners.loading", "Loading partners...")}
                </li>
              ) : partnersError ? (
                <li className="admin-empty">{partnersError}</li>
              ) : partners.length === 0 ? (
                <li className="admin-empty">
                  {t("admin.partners.empty", "No partners yet.")}
                </li>
              ) : (
                partners.map((partner) => {
                  const partnerDetail = partnerDetailsById[partner.id];
                  const isDetailLoading = partnerDetailsLoadingId === partner.id;
                  const commissionRateValue =
                    partnerCommissionEdits[partner.id] ??
                    formatCommissionRateInput(partnerDetail?.commissionRate ?? partner.commissionRate);
                  return (
                  <li key={partner.id} className="admin-item">
                    <div className="admin-item__header">
                      <div>
                        <h3>{partner.name}</h3>
                        <p className="admin-item__meta">{partner.locationName}</p>
                      </div>
                      <span
                        className={`admin-pill admin-pill--${
                          partner.isActive ? "success" : "warning"
                        }`}
                      >
                        {partner.isActive
                          ? t("admin.partners.active", "Active")
                          : t("admin.partners.paused", "Paused")}
                      </span>
                    </div>
                    <div className="admin-item__footer">
                      <span className="admin-item__note">
                        {t("admin.partners.updated", "Updated")}{" "}
                        {formatTimestamp(partner.updatedUtc || partner.createdUtc)}
                      </span>
                      <div className="admin-inline-actions">
                        <button
                          type="button"
                          className="admin-button admin-button--ghost"
                          onClick={() => handlePartnerDetailsToggle(partner)}
                        >
                          {expandedPartnerId === partner.id
                            ? t("admin.actions.hideDetails", "Hide details")
                            : t("admin.actions.viewDetails", "View details")}
                        </button>
                        <button
                          type="button"
                          className="admin-button admin-button--ghost"
                          onClick={() => handlePartnerEdit(partner)}
                        >
                          {t("admin.actions.edit", "Edit")}
                        </button>
                        <button
                          type="button"
                          className="admin-button admin-button--ghost"
                          onClick={() => handlePartnerAccessToggle(partner)}
                        >
                          {partner.isActive
                            ? t("admin.actions.deactivate", "Deactivate")
                            : t("admin.actions.activate", "Activate")}
                        </button>
                        <button
                          type="button"
                          className="admin-button admin-button--danger"
                          onClick={() => handlePartnerDelete(partner)}
                        >
                          {t("admin.actions.deletePartner", "Delete")}
                        </button>
                      </div>
                    </div>
                    {expandedPartnerId === partner.id && (
                      <div className="admin-booking__details">
                        <div>
                          <p className="admin-detail-label">
                            {t("admin.partners.detail.contactName", "Contact name")}
                          </p>
                          <p className="admin-detail-value">
                            {partnerDetail?.contactName ||
                              partner.contactName ||
                              t("admin.bookings.detail.none", "—")}
                          </p>
                        </div>
                        <div>
                          <p className="admin-detail-label">
                            {t("admin.partners.detail.contactEmail", "Contact email")}
                          </p>
                          <p className="admin-detail-value">
                            {partnerDetail?.contactEmail ||
                              partner.contactEmail ||
                              t("admin.bookings.detail.none", "—")}
                          </p>
                        </div>
                        <div>
                          <p className="admin-detail-label">
                            {t("admin.partners.detail.contactPhone", "Contact phone")}
                          </p>
                          <p className="admin-detail-value">
                            {partnerDetail?.contactPhone ||
                              partner.contactPhone ||
                              t("admin.bookings.detail.none", "—")}
                          </p>
                        </div>
                        <div>
                          <p className="admin-detail-label">
                            {t("admin.partners.detail.location", "Location")}
                          </p>
                          <p className="admin-detail-value">{partner.locationName}</p>
                        </div>
                        <div>
                          <p className="admin-detail-label">
                            {t("admin.partners.detail.status", "Status")}
                          </p>
                          <p className="admin-detail-value">
                            {partner.isActive
                              ? t("admin.partners.active", "Active")
                              : t("admin.partners.paused", "Paused")}
                          </p>
                        </div>
                        <div>
                          <p className="admin-detail-label">
                            {t("admin.partners.detail.updated", "Last updated")}
                          </p>
                          <p className="admin-detail-value">
                            {formatTimestamp(partner.updatedUtc || partner.createdUtc)}
                          </p>
                        </div>
                        <div>
                          <p className="admin-detail-label">
                            {t("admin.partners.detail.bookings", "Bookings count")}
                          </p>
                          <p className="admin-detail-value">
                            {isDetailLoading
                              ? t("admin.partners.detail.loading", "Loading")
                              : partnerDetail?.bookingsCount ?? "—"}
                          </p>
                        </div>
                        <div>
                          <p className="admin-detail-label">
                            {t("admin.partners.detail.revenue", "Revenue generated")}
                          </p>
                          <p className="admin-detail-value">
                            {isDetailLoading
                              ? t("admin.partners.detail.loading", "Loading")
                              : partnerDetail
                              ? formatCurrency(partnerDetail.revenueTotal, partnerDetail.currency)
                              : "—"}
                          </p>
                        </div>
                        <div>
                          <p className="admin-detail-label">
                            {t("admin.partners.detail.commissionRate", "Commission rate")}
                          </p>
                          <div className="admin-form__inline">
                            <input
                              className="admin-input admin-input--compact admin-input--mini"
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={commissionRateValue}
                              onChange={(event) =>
                                handlePartnerCommissionChange(partner.id, event.target.value)
                              }
                              disabled={isDetailLoading}
                              aria-label={t(
                                "admin.partners.detail.commissionRateInput",
                                "Commission rate"
                              )}
                            />
                            <button
                              type="button"
                              className="admin-icon-button admin-icon-button--save"
                              onClick={() => handlePartnerCommissionSave(partner)}
                              disabled={isDetailLoading}
                              aria-label={t("admin.actions.save", "Save")}
                              title={t("admin.actions.save", "Save")}
                            >
                              <CiSaveDown2 />
                            </button>
                          </div>
                        </div>
                        <div>
                          <p className="admin-detail-label">
                            {t("admin.partners.detail.commission", "Commission owed")}
                          </p>
                          <p className="admin-detail-value">
                            {isDetailLoading
                              ? t("admin.partners.detail.loading", "Loading")
                              : partnerDetail
                              ? formatCurrency(partnerDetail.commissionOwed, partnerDetail.currency)
                              : "—"}
                          </p>
                        </div>
                        <div className="admin-detail-pin">
                          <p className="admin-detail-label">
                            {t("admin.partners.detail.pin", "PIN code")}
                          </p>
                          <div className="admin-detail-row">
                            <p className="admin-detail-value">
                              {partnerPinLoadingId === partner.id
                                ? t("admin.partners.detail.loading", "Loading")
                                : revealedPins[partner.id] ||
                                  t("admin.bookings.detail.none", "—")}
                            </p>
                            <button
                              type="button"
                              className="admin-icon-button admin-icon-button--pin"
                              onClick={() => handlePartnerResetPin(partner)}
                              disabled={isDetailLoading || partnerPinLoadingId === partner.id}
                              aria-label={t("admin.actions.resetPin", "Reset PIN")}
                              title={t("admin.actions.resetPin", "Reset PIN")}
                            >
                              <FiRefreshCw />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </li>
                );
                })
              )}
            </ul>
            <div className="admin-panel__actions">
              <button
                type="button"
                className="admin-button admin-button--primary"
                onClick={openPartnerForm}
              >
                {t("admin.actions.addPartner", "Add partner")}
              </button>
            </div>
          </article>

          <article ref={locationsRef} className="admin-panel">
            <header>
              <h2>{t("admin.locations.heading", "Locations")}</h2>
            </header>
            {locationFormOpen && (
              <form className="admin-form" onSubmit={handleLocationSave}>
                <div className="admin-form__grid admin-form__grid--wide">
                  <div className="admin-form__row">
                    <label className="admin-form__label" htmlFor="locationName">
                      {t("admin.locationForm.name", "Location name")}
                    </label>
                    <input
                      id="locationName"
                      className="admin-input"
                      value={locationForm.name}
                      onChange={handleLocationFormChange("name")}
                      placeholder={t("admin.locationForm.namePlaceholder", "Old Town Dock")}
                    />
                  </div>
                  <div className="admin-form__row">
                    <label className="admin-form__label" htmlFor="locationAddress">
                      {t("admin.locationForm.address", "Address")}
                    </label>
                    <input
                      id="locationAddress"
                      className="admin-input"
                      value={locationForm.address}
                      onChange={handleLocationFormChange("address")}
                      placeholder={t("admin.locationForm.addressPlaceholder", "Handelskai 265")}
                    />
                  </div>
                  <div className="admin-form__row">
                    <label className="admin-form__label" htmlFor="locationMapsLink">
                      {t("admin.locationForm.mapsLink", "Google Maps link")}
                    </label>
                    <div className="admin-location__maps">
                      <input
                        id="locationMapsLink"
                        className="admin-input"
                        value={mapsLink}
                        onChange={handleMapsLinkChange}
                        placeholder={t(
                          "admin.locationForm.mapsLinkPlaceholder",
                          "https://maps.google.com/..."
                        )}
                      />
                      {mapsLinkError ? (
                        <p className="admin-form__help admin-form__help--error">
                          {mapsLinkError}
                        </p>
                      ) : mapsLinkWarning ? (
                        <p className="admin-form__help admin-form__help--warning">
                          {mapsLinkWarning}
                        </p>
                      ) : mapsLinkLoading ? (
                        <p className="admin-form__help">
                          {t(
                            "admin.locationForm.mapsLinkResolving",
                            "Resolving address from Google Maps..."
                          )}
                        </p>
                      ) : (
                        <p className="admin-form__help">
                          {t(
                            "admin.locationForm.mapsLinkHelp",
                            "Paste a Google Maps link to auto-fill the address."
                          )}
                        </p>
                      )}
                      <div className="admin-location__coords">
                        <div className="admin-form__row">
                          <label className="admin-form__label" htmlFor="locationLatitude">
                            {t("admin.locationForm.latitude", "Latitude")}
                          </label>
                          <input
                            id="locationLatitude"
                            className="admin-input"
                            value={locationForm.latitude}
                            onChange={handleLocationFormChange("latitude")}
                            placeholder={t(
                              "admin.locationForm.latitudePlaceholder",
                              "48.2082"
                            )}
                            inputMode="decimal"
                          />
                        </div>
                        <div className="admin-form__row">
                          <label className="admin-form__label" htmlFor="locationLongitude">
                            {t("admin.locationForm.longitude", "Longitude")}
                          </label>
                          <input
                            id="locationLongitude"
                            className="admin-input"
                            value={locationForm.longitude}
                            onChange={handleLocationFormChange("longitude")}
                            placeholder={t(
                              "admin.locationForm.longitudePlaceholder",
                              "16.3738"
                            )}
                            inputMode="decimal"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="admin-form__row">
                    <label className="admin-form__label">
                      {t("admin.locationForm.hours", "Working hours")}
                    </label>
                    <div className="admin-hours">
                      <div className="admin-hours__days">
                        {LOCATION_WEEK_DAYS.map((day) => {
                          const hasHours = (hoursByDay[day.key] || []).some(
                            (range) => range?.start && range?.end
                          );
                          const isActive = selectedDayKey === day.key;
                          return (
                            <button
                              type="button"
                              key={day.key}
                              className={`admin-hours__day${
                                isActive ? " is-active" : ""
                              }${hasHours ? " is-set" : ""}`}
                              onClick={() => setSelectedDayKey(day.key)}
                              aria-pressed={isActive}
                            >
                              {day.short}
                            </button>
                          );
                        })}
                      </div>
                      <div className="admin-hours__inputs">
                        <span className="admin-hours__label">{selectedDay.label}</span>
                        <div className="admin-hours__ranges">
                          {displayRanges.map((range, rangeIndex) => {
                            const startParts = splitTimeParts(range.start);
                            const endParts = splitTimeParts(range.end);
                            return (
                              <div className="admin-hours__range" key={rangeIndex}>
                                <div className="admin-hours__time">
                                  <select
                                    className="admin-hours__select"
                                    value={startParts.hour}
                                    onChange={handleTimePartChange("start", rangeIndex, "hour")}
                                    aria-label={t(
                                      "admin.locationForm.hoursStartHour",
                                      "Start hour"
                                    )}
                                  >
                                    {TIME_HOURS.map((hour) => (
                                      <option key={hour} value={hour}>
                                        {hour}
                                      </option>
                                    ))}
                                  </select>
                                  <span className="admin-hours__colon">:</span>
                                  <select
                                    className="admin-hours__select"
                                    value={startParts.minute}
                                    onChange={handleTimePartChange("start", rangeIndex, "minute")}
                                    aria-label={t(
                                      "admin.locationForm.hoursStartMinute",
                                      "Start minute"
                                    )}
                                  >
                                    {TIME_MINUTES.map((minute) => (
                                      <option key={minute} value={minute}>
                                        {minute}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <span className="admin-form__divider">to</span>
                                <div className="admin-hours__time">
                                  <select
                                    className="admin-hours__select"
                                    value={endParts.hour}
                                    onChange={handleTimePartChange("end", rangeIndex, "hour")}
                                    aria-label={t(
                                      "admin.locationForm.hoursEndHour",
                                      "End hour"
                                    )}
                                  >
                                    {TIME_HOURS.map((hour) => (
                                      <option key={hour} value={hour}>
                                        {hour}
                                      </option>
                                    ))}
                                  </select>
                                  <span className="admin-hours__colon">:</span>
                                  <select
                                    className="admin-hours__select"
                                    value={endParts.minute}
                                    onChange={handleTimePartChange("end", rangeIndex, "minute")}
                                    aria-label={t(
                                      "admin.locationForm.hoursEndMinute",
                                      "End minute"
                                    )}
                                  >
                                    {TIME_MINUTES.map((minute) => (
                                      <option key={minute} value={minute}>
                                        {minute}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                {selectedRanges.length > 1 && (
                                  <button
                                    type="button"
                                    className="admin-button admin-button--ghost admin-hours__remove"
                                    onClick={() => handleRemoveRange(rangeIndex)}
                                  >
                                    {t("admin.locationForm.hoursRemove", "Remove")}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <div className="admin-hours__actions">
                          <button
                            type="button"
                            className="admin-button admin-button--ghost"
                            onClick={handleAddRange}
                          >
                            {t("admin.locationForm.hoursAdd", "Add")}
                          </button>
                          <button
                            type="button"
                            className="admin-button admin-button--ghost admin-hours__clear"
                            onClick={handleHoursClear}
                            disabled={!selectedRanges.some((range) => range.start && range.end)}
                          >
                            {t("admin.locationForm.hoursClear", "Clear")}
                          </button>
                        </div>
                      </div>
                      <p className="admin-form__help">
                        {locationForm.operatingHours
                          ? t("admin.locationForm.hoursSummary", "Summary: {summary}", {
                              summary: locationForm.operatingHours,
                            })
                          : t("admin.locationForm.hoursEmpty", "No hours set yet.")}
                      </p>
                    </div>
                  </div>
                  <div className="admin-form__row">
                    <span className="admin-form__label">
                      {t("admin.locationForm.bookingTypes", "Booking types")}
                    </span>
                    <div className="admin-location__booking-types">
                      <label
                        className="admin-settings__checkbox admin-location__booking-option admin-location__booking-option--day"
                        htmlFor="locationBookingDay"
                      >
                        <input
                          id="locationBookingDay"
                          type="checkbox"
                          checked={Boolean(locationForm.supportsDayBookings)}
                          onChange={handleLocationFormChange("supportsDayBookings")}
                        />
                        <FiSun aria-hidden="true" />
                        {t("admin.locationForm.dayBooking", "")}
                      </label>
                      <label
                        className="admin-settings__checkbox admin-location__booking-option admin-location__booking-option--night"
                        htmlFor="locationBookingNight"
                      >
                        <input
                          id="locationBookingNight"
                          type="checkbox"
                          checked={Boolean(locationForm.supportsNightBookings)}
                          onChange={handleLocationFormChange("supportsNightBookings")}
                        />
                        <FiMoon aria-hidden="true" />
                        {t("admin.locationForm.nightBooking", "")}
                      </label>
                    </div>
                  </div>
                  <div className="admin-form__row">
                    <label className="admin-form__label" htmlFor="locationBlockedDate">
                      {t("admin.locationForm.blockedDates", "Blocked dates")}
                    </label>
                    <div className="admin-location__maps">
                      <div className="admin-location__coords">
                        <div className="admin-form__row">
                          <input
                            id="locationBlockedDate"
                            className="admin-input"
                            type="date"
                            value={blockedDateInput}
                            onChange={(event) => setBlockedDateInput(event.target.value)}
                          />
                        </div>
                        <div className="admin-form__row">
                          <button
                            type="button"
                            className="admin-button admin-button--ghost"
                            onClick={handleBlockedDateAdd}
                            disabled={!toDateKey(blockedDateInput)}
                          >
                            {t("admin.locationForm.blockedDatesAdd", "Add blocked date")}
                          </button>
                        </div>
                      </div>
                      <p className="admin-form__help">
                        {t(
                          "admin.locationForm.blockedDatesHelp",
                          "Clients will see these exact dates crossed out and unavailable."
                        )}
                      </p>
                      {blockedDates.length === 0 ? (
                        <p className="admin-form__help">
                          {t("admin.locationForm.blockedDatesEmpty", "No blocked dates set.")}
                        </p>
                      ) : (
                        <div className="admin-tags">
                          {blockedDates.map((date) => (
                            <button
                              key={date}
                              type="button"
                              className="admin-tag"
                              onClick={() => handleBlockedDateRemove(date)}
                              title={t("admin.locationForm.blockedDatesRemove", "Remove blocked date")}
                            >
                              {date} ×
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="admin-form__row">
                    <label className="admin-form__label" htmlFor="locationLeadTimeHours">
                      {t("admin.locationForm.leadTimeHours", "Booking time blocker (hours)")}
                    </label>
                    <input
                      id="locationLeadTimeHours"
                      className="admin-input"
                      type="number"
                      min="0"
                      step="1"
                      inputMode="numeric"
                      value={locationForm.bookingLeadTimeHours}
                      onChange={handleLocationFormChange("bookingLeadTimeHours")}
                      placeholder={t("admin.locationForm.leadTimeHoursPlaceholder", "12")}
                    />
                    <p className="admin-form__help">
                      {t(
                        "admin.locationForm.leadTimeHoursHelp",
                        "Example: 12 means clients cannot book this location less than 12 hours before start time."
                      )}
                    </p>
                  </div>
                  <div className="admin-form__row">
                    <label className="admin-form__label" htmlFor="locationStatus">
                      {t("admin.locationForm.status", "Status")}
                    </label>
                    <select
                      id="locationStatus"
                      className="admin-select"
                      value={locationForm.isActive ? "active" : "inactive"}
                      onChange={handleLocationFormChange("isActive")}
                    >
                      <option value="active">{t("admin.locationForm.active", "Active")}</option>
                      <option value="inactive">
                        {t("admin.locationForm.inactive", "Inactive")}
                      </option>
                    </select>
                  </div>
                </div>
                <div className="admin-form__actions">
                  <button
                    type="submit"
                    className="admin-button admin-button--primary"
                    disabled={isSavingLocation}
                  >
                    {locationEditingId
                      ? t("admin.locationForm.update", "Update location")
                      : t("admin.locationForm.save", "Save location")}
                  </button>
                  <button
                    type="button"
                    className="admin-button admin-button--ghost"
                    onClick={() => {
                      setLocationFormOpen(false);
                      setLocationEditingId(null);
                      setLocationForm({ ...DEFAULT_LOCATION_FORM });
                    }}
                  >
                    {t("admin.locationForm.cancel", "Cancel")}
                  </button>
                </div>
              </form>
            )}
            <ul className="admin-list">
              {locationsLoading ? (
                <li className="admin-empty">
                  {t("admin.locations.loading", "Loading locations...")}
                </li>
              ) : locationsError ? (
                <li className="admin-empty">{locationsError}</li>
              ) : locations.length === 0 ? (
                <li className="admin-empty">
                  {t("admin.locations.empty", "No locations configured yet.")}
                </li>
              ) : (
                locations.map((location) => {
                  const inventoryTags = location.inventory.map((item) => {
	                    const parts = [`${item.equipmentTypeName} · ${item.quantity}`];
	                    if (item.maintenanceQuantity) parts.push(`Maint ${item.maintenanceQuantity}`);
	                    if (item.lostQuantity) parts.push(`Lost ${item.lostQuantity}`);
	                    return parts.join(" · ");
	                  });
                      return (
                    <li key={location.id} className="admin-item">
                      <div className="admin-item__header">
                        <div>
                          <h3 className="admin-location__title">
                            <span>{location.name}</span>
                            <span className="admin-location__type-icons" aria-hidden="true">
                              {location.supportsDayBookings && <FiSun />}
                              {location.supportsNightBookings && <FiMoon />}
                            </span>
                          </h3>
                          <p className="admin-item__meta">{formatLocationAddress(location)}</p>
                        </div>
                        <span
                          className={`admin-pill admin-pill--${
                            location.isActive ? "success" : "muted"
                          }`}
                        >
                          {location.isActive
                            ? t("admin.locations.active", "Active")
                            : t("admin.locations.inactive", "Inactive")}
                        </span>
                      </div>
                      <div className="admin-item__footer">
                      <span className="admin-item__note">
                        {location.timezoneId} · {location.country}
                      </span>
                      <div className="admin-inline-actions">
                        <button
                          type="button"
                          className="admin-button admin-button--ghost"
                          onClick={() => handleLocationDetailsToggle(location.id)}
                        >
                          {expandedLocationId === location.id
                            ? t("admin.actions.hideDetails", "Hide details")
                            : t("admin.actions.viewDetails", "View details")}
                        </button>
                        <button
                          type="button"
                          className="admin-button admin-button--ghost"
                          onClick={() => handleLocationEdit(location)}
                        >
                            {t("admin.actions.editLocation", "Edit")}
                          </button>
                        <button
                          type="button"
                          className="admin-button admin-button--outline"
                          onClick={() => handleLocationToggleActive(location)}
                        >
                          {location.isActive
                            ? t("admin.actions.deactivate", "Deactivate")
                            : t("admin.actions.activate", "Activate")}
                        </button>
                        <button
                          type="button"
                          className="admin-button admin-button--danger"
                          onClick={() => handleLocationDelete(location)}
                        >
                          {t("admin.actions.deleteLocation", "Delete")}
                        </button>
                      </div>
                    </div>
                    {expandedLocationId === location.id && (
                      <div className="admin-booking__details">
                        <div>
                          <p className="admin-detail-label">
                            {t("admin.locations.detail.address", "Address")}
                          </p>
                          <p className="admin-detail-value">
                            {formatLocationAddress(location) ||
                              t("admin.bookings.detail.none", "—")}
                          </p>
                        </div>
                        <div>
                          <p className="admin-detail-label">
                            {t("admin.locations.detail.country", "Country")}
                          </p>
                          <p className="admin-detail-value">
                            {location.country || t("admin.bookings.detail.none", "—")}
                          </p>
                        </div>
                        <div>
                          <p className="admin-detail-label">
                            {t("admin.locations.detail.hours", "Working hours")}
                          </p>
                          <p className="admin-detail-value">
                            {location.operatingHours || t("admin.bookings.detail.none", "—")}
                          </p>
                        </div>
                        <div>
                          <p className="admin-detail-label">
                            {t("admin.locations.detail.blockedDates", "Blocked dates")}
                          </p>
                          {location.blockedDates?.length ? (
                            <div className="admin-tags">
                              {location.blockedDates.map((date) => (
                                <span key={date} className="admin-tag">
                                  {date}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="admin-detail-value">
                              {t("admin.bookings.detail.none", "—")}
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="admin-detail-label">
                            {t("admin.locations.detail.leadTimeHours", "Booking time blocker")}
                          </p>
                          <p className="admin-detail-value">
                            {Number(location.bookingLeadTimeHours ?? 0) > 0
                              ? `${location.bookingLeadTimeHours}h`
                              : t("admin.bookings.detail.none", "—")}
                          </p>
                        </div>
                        <div>
                          <p className="admin-detail-label">
                            {t("admin.locations.detail.timezone", "Timezone")}
                          </p>
                          <p className="admin-detail-value">
                            {location.timezoneId || t("admin.bookings.detail.none", "—")}
                          </p>
                        </div>
                        <div>
                          <p className="admin-detail-label">
                            {t("admin.locations.detail.status", "Status")}
                          </p>
                          <p className="admin-detail-value">
                            {location.isActive
                              ? t("admin.locations.active", "Active")
                              : t("admin.locations.inactive", "Inactive")}
                          </p>
                        </div>
                        <div>
                          <p className="admin-detail-label">
                            {t("admin.locations.detail.inventory", "Inventory")}
                          </p>
                          {inventoryTags.length === 0 ? (
                            <p className="admin-detail-value">
                              {t("admin.locations.detail.noInventory", "No inventory")}
                            </p>
                          ) : (
                            <div className="admin-tags">
                              {inventoryTags.map((tag) => (
                                <span key={tag} className="admin-tag">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </li>
                );
              })
              )}
            </ul>
            <div className="admin-panel__actions">
              <button
                type="button"
                className="admin-button admin-button--primary"
                onClick={openLocationForm}
              >
                {t("admin.actions.addLocation", "Add location")}
              </button>
              <button
                type="button"
                className="admin-button admin-button--ghost"
                onClick={handleLocationsRefresh}
              >
                {t("admin.actions.refreshLocations", "Refresh list")}
              </button>
            </div>
          </article>
        </section>
            </div>
            
);
};

export default AdminLocPartnersTab;

export {
  LOCATION_WEEK_DAYS,
  TIME_HOURS,
  TIME_MINUTES,
  createEmptyHours,
  normalizeTime,
  splitTimeParts,
  parseOperatingHours,
  formatOperatingHours,
  parseMapsLinkCandidate,
  isShortMapsLink,
};
